import moment from 'moment-timezone';
import { isEmpty } from 'lodash';
import { humanize, granularity } from '@utils';
import { GranularityMap } from '@components/common/GranularitySelect/utils';
import { isAppModule, isEcoServerModule, isOdpModule } from '@utils/constants';

const timeBounds = {
  days: 24 * 60 * 60 * 1000,
  hours: 60 * 60 * 1000,
  mins: 60 * 1000,
  secs: 1000,
};

const roundToTwo = (val) => Math.round((val + Number.EPSILON) * 100) / 100;

export const formatTime = (value) => {
  let out = [value, 'ms'];

  if (value > timeBounds.days) {
    out = [value / timeBounds.days, 'd'];
  } else if (value > timeBounds.hours) {
    out = [value / timeBounds.hours, 'h'];
  } else if (value > timeBounds.mins) {
    out = [value / timeBounds.mins, 'm'];
  } else if (value > timeBounds.secs) {
    out = [value / timeBounds.secs, 's'];
  }

  return roundToTwo(out[0]) + out[1];
};

export const humanizeHoc = (value, unit) => {
  switch (unit) {
    case 'Kbps':
      return humanize(value * 1000, 'bps') + 'bps';
    case 'min':
      return formatTime(value * 60 * 1000);
    case 'sec':
      return formatTime(value * 1000);
    case 'ms':
      return formatTime(value);
    case 'fps':
    case '%':
      return `${value}${unit}`;
    case 'unitless':
    case 'ratio':
      return value;
  }
};

export const axisFormatter = (value, unit) => {
  let hzValue = value;

  switch (unit) {
    case 'Kbps':
    case 'sec':
    case 'ms':
    case 'min':
    case 'fps': {
      // middle value
      if (String(value).includes('-')) {
        let [start, end] = value.split('-').map(Number);
        start = humanizeHoc(start, unit);
        end = humanizeHoc(end, unit);
        hzValue = `${start} - ${end}`;
      }
      // last value
      else if (String(value).includes('>=')) {
        let last: any = Number(value.slice(2));
        last = humanizeHoc(last, unit);
        hzValue = `>= ${last}`;
      }
      // last value
      else if (String(value).includes('>')) {
        let last: any = Number(value.slice(1));
        last = humanizeHoc(last, unit);
        hzValue = `> ${last}`;
      }
      // first value
      else {
        let first: any = Number(value);
        first = humanizeHoc(first, unit);
        hzValue = `${first}`;
      }
      break;
    }
    case 'unitless':
    case 'ratio': {
      break;
    }
    // default behaviour
    case '': {
      hzValue = humanize(Number(value), unit) + unit;
      break;
    }
  }

  return hzValue;
};

// generate empty response to help identify gaps
export const generateFillerData = (interval) => {
  const { from, granularity: granularityVal } = interval;
  let { to } = interval;

  const dataGranularity = granularityVal || granularity(from, to, true, false, interval.shortcut);
  const { step, unit } = GranularityMap[dataGranularity];

  // datepicker includes last minute for some intervals but excludes them for EoD cases
  // this workaround adds a minute to ensure diag chart plots the last point
  const toObj = moment(to);
  if (toObj.hour() === 23 && toObj.minute() === 59) {
    to = moment(to).add(1, 'minute');
  }

  const diff = moment(to).diff(moment(from), unit);

  const data: Array<[string, number | null]> = [];
  for (let index = 0; index < diff; index = index + step) {
    data.push([moment(from).add(index, unit).toISOString(), null]);
  }

  return data;
};

// backend auto adjusts query interval on certain scenarios
// considering user interval for plotting could break the alignment on different granularities
// hence using start time from backend and end time as the max of user or response value
export const getPlotInterval = (interval, data) => {
  try {
    if (isEmpty(data)) {
      return interval;
    }

    const queryFrom = data[0].timestamp;
    const queryTo = data[data.length - 1].timestamp;

    return {
      from: queryFrom,
      to: moment(interval.to).isSameOrAfter(queryTo) ? interval.to : queryTo,
      granularity: interval.granularity,
    };
  } catch (err) {
    return interval;
  }
};

// Temp fix as app has a different time format now
const buildAppLookupData = (data) => {
  const dataLookup = {};
  data.forEach((row) => {
    dataLookup[moment(row.timestamp).toISOString()] = row.results;
  });
  return dataLookup;
};

const buildLookupData = (data) => {
  const dataLookup = {};
  data.forEach((row) => {
    dataLookup[row.timestamp] = row.results;
  });
  return dataLookup;
};

export function formatTimeseriesData({ metrics, data, interval, useAppLookupForPrompt = false }) {
  const formattedData = {};

  metrics.forEach((m) => (formattedData[m] = []));

  const plotInterval = getPlotInterval(interval, data);
  const fillerData = generateFillerData(plotInterval);
  // response timestamp lookup
  const dataLookup =
    useAppLookupForPrompt || isAppModule || isOdpModule || isEcoServerModule
      ? buildAppLookupData(data)
      : buildLookupData(data);

  // fill gaps and format response
  fillerData.forEach(([time, value]) => {
    const row = time in dataLookup ? dataLookup[time] : {};
    metrics.forEach((m) => {
      formattedData[m].push([time, m in row ? row[m] : value]);
    });
  });

  return formattedData;
}
